import { useState, useEffect } from 'react';
import { Loader2, Check, Ban, SlidersHorizontal } from 'lucide-react';
import ModalShell from '../../../components/ui/ModalShell';
import Dropdown from '../../../components/receptura/Dropdown';
import { api, AKCENT as SECTION_COLOR } from '../../../lib/spizirnaUi';

/** Volba „bez varianty" — `Porce` míří na recepturu, která variantu nemá. */
const BEZ_VARIANTY = '__bez__';

/**
 * Slovník variant Objedname → naše.
 *
 * Tady bydlí jistota celého párování. `150g → Malé` a `0,5L → Velká`
 * nepřeklene žádná podobnost řetězců a ani se o to nemá pokoušet — ale obě
 * množiny jsou **konečné** (Objedname 61, my 30), takže se dají jednou
 * vyjmenovat a odklepnout. Od té chvíle to není odhad.
 *
 * Řadí se podle toho, **kolik názvů na které variantě visí**. Tři řádky nahoře
 * odemykaly v měření 73 názvů; abecedně seřazený číselník by začínal
 * u `-100 Kč`, což je sleva se třemi výskyty.
 *
 * Je to modal, ne inspektor: nastavení, které se otevře, dodělá a zavře,
 * a u kterého se nepotřebuje vidět seznam za ním (norma §1f).
 */
export default function ModalVarianty({ isDark, styly, onZavrit }) {
    const { secondaryColor, mutedColor } = styly;
    const [radky, setRadky] = useState(null);
    const [nase, setNase] = useState([]);
    const [uklada, setUklada] = useState(null);

    useEffect(() => {
        Promise.all([api('/api/objedname/varianty'), api('/api/objedname/varianty/nase')])
            .then(([v, n]) => { setRadky(v); setNase(n); })
            .catch(() => setRadky([]));
    }, []);

    const uloz = async (radek, patch) => {
        setUklada(radek.id);
        try {
            const novy = await api(`/api/objedname/varianty/${radek.id}`, 'PUT', patch);
            setRadky((r) => r.map((x) => (x.id === radek.id
                ? { ...x, nase_varianta: novy.nase_varianta, stav: novy.stav }
                : x)));
        } finally {
            setUklada(null);
        }
    };

    const moznosti = [
        { value: '', label: '— nerozhodnuto —' },
        { value: BEZ_VARIANTY, label: 'Bez varianty (receptura variantu nemá)' },
        ...nase.map((v) => ({ value: v, label: v })),
    ];

    const ceka = (radky ?? []).filter((r) => r.stav === 'ceka');
    const zbytek = (radky ?? []).filter((r) => r.stav !== 'ceka');

    return (
        <ModalShell open onClose={onZavrit} isDark={isDark} nadpis="Slovník variant"
            ikona={SlidersHorizontal} barva={SECTION_COLOR} sirka={620}>
            <div style={{ padding: 16, maxHeight: '64vh', overflowY: 'auto' }}>
                <p className="text-[12px] leading-relaxed mb-3" style={{ color: secondaryColor }}>
                    Co v Objedname stojí v závorce, přeložené na naši variantu. Párovač
                    se ptá jen sem — <b>co tu není, neodhaduje</b>, ale pošle název do fronty.
                    Proto je tenhle seznam jistota, ne podobnost.
                </p>

                {radky === null ? (
                    <div className="flex justify-center py-10">
                        <Loader2 size={18} className="animate-spin" style={{ color: mutedColor }} />
                    </div>
                ) : (
                    <>
                        {ceka.length > 0 && (
                            <Sekce popis={`Čeká na rozhodnutí (${ceka.length})`} mutedColor={mutedColor}>
                                {ceka.map((r) => (
                                    <Radek key={r.id} radek={r} moznosti={moznosti} isDark={isDark}
                                        styly={styly} uklada={uklada === r.id} onUlozit={uloz} />
                                ))}
                            </Sekce>
                        )}
                        {zbytek.length > 0 && (
                            <Sekce popis={`Hotové (${zbytek.length})`} mutedColor={mutedColor}>
                                {zbytek.map((r) => (
                                    <Radek key={r.id} radek={r} moznosti={moznosti} isDark={isDark}
                                        styly={styly} uklada={uklada === r.id} onUlozit={uloz} />
                                ))}
                            </Sekce>
                        )}
                    </>
                )}
            </div>
        </ModalShell>
    );
}

function Sekce({ popis, mutedColor, children }) {
    return (
        <div className="mb-4">
            <div className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: mutedColor }}>
                {popis}
            </div>
            <div className="flex flex-col gap-1">{children}</div>
        </div>
    );
}

function Radek({ radek, moznosti, isDark, styly, uklada, onUlozit }) {
    const { mutedColor, secondaryColor } = styly;
    const hodnota = radek.stav === 'potvrzeno'
        ? (radek.nase_varianta ?? BEZ_VARIANTY)
        : (radek.nase_varianta ?? '');

    const zmen = (v) => {
        if (v === '') return onUlozit(radek, { nase_varianta: null, stav: 'ceka' });
        onUlozit(radek, {
            nase_varianta: v === BEZ_VARIANTY ? null : v,
            stav: 'potvrzeno',
        });
    };

    return (
        <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg"
            style={{
                background: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.02)',
                border: `1px solid ${radek.stav === 'potvrzeno' ? 'rgba(16,185,129,0.28)' : 'rgba(160,160,180,0.18)'}`,
            }}>
            <div className="min-w-0" style={{ flex: '0 0 150px' }}>
                <div className="text-[12px] font-mono truncate" style={{ color: 'var(--text-primary)' }}>
                    {radek.externi}
                </div>
                {radek.blokuje_nazvu > 0 && (
                    <div className="text-[10px]" style={{ color: SECTION_COLOR }}>
                        blokuje {radek.blokuje_nazvu}{' '}
                        {radek.blokuje_nazvu === 1 ? 'název' : radek.blokuje_nazvu < 5 ? 'názvy' : 'názvů'}
                    </div>
                )}
            </div>

            <span style={{ color: mutedColor, flexShrink: 0 }}>→</span>

            <div className="flex-1 min-w-0">
                <Dropdown value={hodnota} onChange={zmen} options={moznosti} isDark={isDark}
                    height={30} accent={SECTION_COLOR} placeholder="— nerozhodnuto —" />
            </div>

            <div style={{ width: 22, flexShrink: 0, textAlign: 'center' }}>
                {uklada ? (
                    <Loader2 size={13} className="animate-spin" style={{ color: mutedColor }} />
                ) : radek.stav === 'potvrzeno' ? (
                    <Check size={14} strokeWidth={3} style={{ color: '#10B981' }} />
                ) : (
                    <button onClick={() => onUlozit(radek, { nase_varianta: null, stav: 'ignorovat' })}
                        title="Není to varianta — sleva, obal a podobné"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 0 }}>
                        <Ban size={13} />
                    </button>
                )}
            </div>
        </div>
    );
}
